import React, { useEffect, useState } from "react";
import { adminLeads, adminUpdateLead, adminDeleteLead, adminQuoteSessions } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Trash2, MousePointer2 } from "lucide-react";

const STATUSES = ["New", "Contacted", "Proposal Sent", "Deposit Paid", "In Progress", "Completed", "Lost"];
const TYPE_COLORS = {
  project: "bg-primary text-white",
  audit: "bg-amber-500 text-white",
  quote: "bg-blue-600 text-white",
  care: "bg-emerald-600 text-white",
  playbook: "bg-violet-600 text-white",
  contact: "bg-secondary text-foreground",
};

function LeadCard({ lead, onChanged }) {
  const setStatus = async (status) => {
    try { await adminUpdateLead(lead.id, { status }); toast.success(`Marked "${status}"`); onChanged(); }
    catch { toast.error("Update failed"); }
  };
  const saveNotes = async (notes) => {
    try { await adminUpdateLead(lead.id, { notes }); } catch { /* silent */ }
  };
  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await adminDeleteLead(lead.id); toast.success("Deleted"); onChanged(); }
    catch { toast.error("Delete failed"); }
  };
  return (
    <article className="border border-border p-5 bg-card" data-testid={`lead-${lead.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${TYPE_COLORS[lead.type] || TYPE_COLORS.contact}`}>{lead.type}</span>
            <p className="font-display text-lg leading-tight">{lead.name}</p>
            {lead.company && <span className="text-sm text-muted-foreground">· {lead.company}</span>}
          </div>
          <a href={`mailto:${lead.email}`} className="text-primary text-sm link-underline">{lead.email}</a>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {lead.service && <span>Service: <b className="text-foreground">{lead.service}</b></span>}
            {lead.country && <span>Country: <b className="text-foreground">{lead.country}</b></span>}
            {lead.industry && <span>Industry: <b className="text-foreground">{lead.industry}</b></span>}
            {lead.budget && <span>Budget: <b className="text-foreground">{lead.budget}</b></span>}
            {lead.timeline && <span>Timeline: <b className="text-foreground">{lead.timeline}</b></span>}
            {lead.website && <span>Site: <a href={lead.website} target="_blank" rel="noreferrer" className="text-primary link-underline">{lead.website}</a></span>}
            {lead.estimate?.total ? <span>Estimate: <b className="text-primary">${lead.estimate.total} (dep. ${lead.estimate.deposit})</b></span> : null}
          </div>
          {lead.message && <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line line-clamp-3">{lead.message}</p>}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <select
            value={lead.status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid={`lead-status-${lead.id}`}
            className={`text-xs font-mono uppercase tracking-widest border px-2.5 py-1.5 bg-background outline-none cursor-pointer ${lead.status === "New" ? "border-primary text-primary" : "border-border"}`}
          >
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <span className="font-mono text-[10px] text-muted-foreground">{(lead.created_at || "").slice(0, 10)}</span>
          <button onClick={remove} aria-label="Delete lead" className="text-muted-foreground hover:text-destructive transition p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <textarea
        defaultValue={lead.notes || ""}
        onBlur={(e) => e.target.value !== (lead.notes || "") && saveNotes(e.target.value)}
        placeholder="Private notes…"
        rows={1}
        className="mt-3 w-full bg-secondary/60 px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary text-muted-foreground"
      />
    </article>
  );
}

export default function AdminLeads() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [abandoned, setAbandoned] = useState([]);
  const load = () => {
    adminLeads().then(setItems).catch(() => setItems([]));
    adminQuoteSessions().then((rows) => setAbandoned(rows.filter((r) => !r.completed))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!items) return <p className="text-muted-foreground">Loading leads…</p>;
  const shown = filter === "all" ? items : items.filter((l) => l.type === filter);
  const newCount = items.filter((l) => l.status === "New").length;

  return (
    <div data-testid="admin-leads">
      <h2 className="font-display text-3xl mb-2 flex items-center gap-3"><Inbox className="w-7 h-7 text-primary" /> Leads ({items.length})</h2>
      <p className="text-muted-foreground text-sm mb-6">
        {newCount} new. Every project start, audit request, quote request, and contact-form enquiry lands here.
        Move each lead through the pipeline with the status dropdown.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "project", "audit", "quote", "care", "playbook", "contact"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`lead-filter-${f}`}
            className={`px-3.5 py-1.5 text-xs font-mono uppercase tracking-widest border transition ${filter === f ? "bg-primary text-white border-primary" : "border-border hover:border-primary"}`}>
            {f}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {shown.length === 0 && <p className="p-6 border border-border text-muted-foreground">No leads yet in this view.</p>}
        {shown.map((l) => <LeadCard key={l.id} lead={l} onChanged={load} />)}
      </div>

      {abandoned.length > 0 && (
        <div className="mt-12" data-testid="abandoned-quotes">
          <h3 className="font-display text-2xl mb-2 flex items-center gap-2"><MousePointer2 className="w-5 h-5 text-primary" /> Abandoned quotes with email ({abandoned.length})</h3>
          <p className="text-sm text-muted-foreground mb-5">These visitors started a quote and shared their email but never checked out. A friendly follow-up converts well.</p>
          <div className="border border-border divide-y divide-border">
            {abandoned.map((q) => (
              <div key={q.id} className="flex items-center justify-between gap-4 p-4 text-sm">
                <div className="min-w-0">
                  <a href={`mailto:${q.email}?subject=Your%20website%20estimate`} className="text-primary link-underline font-medium">{q.email}</a>
                  {q.estimate?.total && <span className="text-muted-foreground"> — estimated ${q.estimate.total}</span>}
                </div>
                <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{(q.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
